import styled from 'styled-components';
import { useCallback } from 'react';
import { firefly } from '@paradigm/design-system/src/colors';
import {
  useQuoteBuilderModalMethods,
  useQuoteBuilderModalState,
} from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/modal-context';
import {
  RfqProvider,
  useGrfqContext,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-context';

import {
  QuoteBuilderProvider,
  useQuoteBuilderActions,
} from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/form-context';

import HeadedModal from '@paradigm/design-system/src/components/HeadedModal';
import RfqStrategySummary, {
  ProductInfo,
} from '#/components/UnifiedRfqs/components/RfqStrategySummary';
import Grid from '#/components/UnifiedRfqs/components/Grid';

import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/view';
import { useGrfqBbo } from '@paradigm/features/src/unified-rfqs/ui/rfqs/bbo';
import { useUnifiedMarketsV2PricingAlgorithmFeatureFlag } from '@paradigm/features/src/feature-flags/context';
import { AccountSelectContainer } from '#/components/account-selection/AccountSelect';
import { quoteModalSize } from '#/components/UnifiedRfqs/shared';
import StrategyGreeks from '#/components/UnifiedRfqs/components/StrategyGreeks';
import { Divider } from '#/components/UnifiedRfqs/drfq/common';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { MobileRfqInfo } from '#/components/UnifiedRfqs/components/DetailsModal';
import PricingOptionsContainer from '#/components/UnifiedRfqs/components/PricingOptionsContainer';
import QuoteBuilderForm from '#/components/UnifiedRfqs/grfq/quote-builder/QuoteBuilderForm';
import LegsTable from '#/components/UnifiedRfqs/grfq/quote-builder/LegsTable';
import {
  InputLegsPrice,
  MarkPriceLegs,
} from '#/components/UnifiedRfqs/grfq/quote-builder/InputLegsPricesTable';
import BidsAndAsksTables from '#/components/UnifiedRfqs/grfq/quote-builder/BidsAndAsksTables';
import AccountSelect from '#/components/UnifiedRfqs/grfq/quote-builder/AccountSelect';
import {
  MobileCardContainer,
  MobileGrfqDate,
  MobileLegsTable,
  MobileMarkPrice,
} from '#/components/UnifiedRfqs/grfq/quote-builder/mobile';
import { Grfq } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import NoRfqFound from '#/components/UnifiedRfqs/quote-builder/NoRfqFound';
import Button from '@paradigm/design-system/src/components/Button';
import BboViewProvider from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import {
  TrackingCtxProvider,
  useTrackAction,
} from '@paradigm/features/src/analytics';

export default function QuoteBuilder() {
  const { rfqId, isOpen } = useQuoteBuilderModalState();

  if (!isOpen || rfqId == null) return null;

  return (
    <RfqProvider rfqId={rfqId}>
      <QuoteBuilderModal />
    </RfqProvider>
  );
}

function QuoteBuilderModal() {
  const { rfq } = useGrfqContext();
  const { isOpen } = useQuoteBuilderModalState();
  const { close } = useQuoteBuilderModalMethods();

  if (rfq == null) return <NoRfqFound isOpen={isOpen} close={close} />;

  return (
    <QuoteBuilderProvider>
      <BboViewProvider>
        <TrackingCtxProvider value="ob quote builder">
          <QuoteBuilderModalContent grfq={rfq} />
        </TrackingCtxProvider>
      </BboViewProvider>
    </QuoteBuilderProvider>
  );
}

function QuoteBuilderModalContent({ grfq }: { grfq: Grfq }) {
  const { close } = useQuoteBuilderModalMethods();
  const { isOpen, legPricingEnabled } = useQuoteBuilderModalState();
  const { isMultiLegOption } = useQuoteBuilderView();
  const isMobile = useIsMobile();
  const { changePrice } = useQuoteBuilderActions();
  const trackAction = useTrackAction();
  const handleClose = useCallback(() => {
    trackAction('close modal');
    close();
  }, [close, trackAction]);

  return (
    <HeadedModal
      title="Order"
      isOpen={isOpen}
      close={handleClose}
      leftInfo={<LeftInfo grfq={grfq} />}
      width={quoteModalSize(legPricingEnabled, false, isMultiLegOption)}
      fade={false}
      aria-label="unified rfq quote builder modal"
    >
      {isMobile ? (
        <>
          <MobileRfqInfo
            description={grfq.description}
            venue={grfq.venue}
            productCode={grfq.product_codes[0]}
          />
          <div css="padding: 1rem; padding-top: 0;">
            <MobileMarkPrice />
            <MobileLegsTable />
            <MobileCardContainer>
              <Greeks grfq={grfq} />
            </MobileCardContainer>
            <BidsAndAsksTables />
            <MobileCardContainer>
              <PricingOptions />
              <AccountSelect />
              <QuoteBuilderForm />
            </MobileCardContainer>
          </div>
        </>
      ) : (
        <FlexContainer>
          <Grid cols={1} css="padding: 1rem; flex: 1;">
            <RfqStrategySummary rfq={grfq} handleMarkPriceClick={changePrice} />
            <FlexContainer>
              <LegsTable />
              {legPricingEnabled && (
                <>
                  {isMultiLegOption && <MarkPriceLegs />}
                  <InputLegsPrice />
                </>
              )}
            </FlexContainer>
            <BidsAndAsksTables />
          </Grid>
          <RightContainer>
            <PricingOptions />
            <AccountSelect />
            <Greeks grfq={grfq} />
            <Divider />
            <QuoteBuilderForm />
          </RightContainer>
        </FlexContainer>
      )}
    </HeadedModal>
  );
}

function PricingOptions() {
  const isV2PricingAlgoEnabled =
    useUnifiedMarketsV2PricingAlgorithmFeatureFlag();
  const { legPricingEnabled } = useQuoteBuilderModalState();
  const { setLegPricing } = useQuoteBuilderModalMethods();
  const { isSingleLeg } = useQuoteBuilderView();
  const trackAction = useTrackAction();
  const toggleLegPricing = useCallback(() => {
    trackAction('toggle leg pricing', {
      isLegPricingEnabled: !legPricingEnabled,
    });
    if (isSingleLeg) return;
    setLegPricing(!legPricingEnabled);
  }, [isSingleLeg, legPricingEnabled, setLegPricing, trackAction]);

  return isV2PricingAlgoEnabled ? (
    <PricingOptionsContainer>
      <Button
        size="small"
        ariaLabel="select leg prices mode"
        variant={legPricingEnabled ? 'pill' : 'link'}
        onClick={toggleLegPricing}
        disabled={isSingleLeg}
      >
        Leg Prices
      </Button>
      <Button
        size="small"
        ariaLabel="select two-way mode"
        variant={!legPricingEnabled ? 'pill' : 'link'}
        onClick={toggleLegPricing}
      >
        Two-Way
      </Button>
    </PricingOptionsContainer>
  ) : null;
}

function Greeks({ grfq }: { grfq: Grfq }) {
  const bbo = useGrfqBbo();

  return (
    <StrategyGreeks bbo={bbo.data} venue={grfq.venue} hideOnMobile={false} />
  );
}

function LeftInfo({ grfq }: { grfq: Grfq }) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <MobileGrfqDate grfq={grfq} />
  ) : (
    <ProductInfo venue={grfq.venue} productCode={grfq.product_codes[0]} />
  );
}

const FlexContainer = styled.div`
  display: flex;
`;

export const RightContainer = styled.div`
  width: 232px;
  background-color: ${firefly};
  border-radius: 0 0 10px 0;
  padding: 1rem;
  display: grid;
  flex-direction: column;
  gap: 1rem;
  grid-template-rows: 1.5rem 1fr;

  ${AccountSelectContainer} {
    margin-bottom: 0;
  }

  ${Divider} {
    width: 100%;
    margin-bottom: 1rem;

    &:before {
      background: linear-gradient(
        359deg,
        rgba(54, 121, 221, 0) 0%,
        rgba(54, 121, 221, 1) 50%,
        rgba(54, 121, 221, 0) 100%
      );
    }
  }
`;
